import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../api/api";
import { asApiError } from "../../api/client";
import { ErrorBox, Spinner } from "../../components/ui";
import { AuthShell } from "./AuthShell";

export default function ForgotPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.forgot(email);
      // The generic confirmation is for a 2xx only: the endpoint answers
      // the same whether or not the address exists, so this reveals nothing.
      setSent(true);
    } catch (err) {
      // A refusal (rate limit, invalid address, server down) is NOT a sent
      // request — say so, or the user waits for an email that never comes.
      const apiErr = asApiError(err);
      setError(
        apiErr.status === 429
          ? t("auth.tooManyRequests", {
              defaultValue: "Too many requests. Please wait a while and try again.",
            })
          : apiErr.message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t("auth.forgotTitle")}>
      {sent ? (
        <p className="text-sm text-muted">{t("auth.forgotSent")}</p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label" htmlFor="email">{t("auth.email")}</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              className="input mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {error && <ErrorBox message={error} />}
          <button type="submit" className="btn-primary w-full justify-center" disabled={busy}>
            {busy ? <Spinner /> : t("auth.forgotTitle")}
          </button>
        </form>
      )}
      <div className="mt-4 text-sm">
        <Link className="text-brand hover:underline" to="/login">{t("auth.signIn")}</Link>
      </div>
    </AuthShell>
  );
}
